/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { KeycloakService } from '../services/keycloak-service';
import { EnvironmentService } from '../services/environment.service';
import { KeycloakOidcProviderService } from '../services/keycloak-oidc-provider.service';
import { SessionGuardService } from '../../session-guard.service';
import { InitializationService } from '../services/initialization.service';
import { KeycloakConfig } from 'keycloak-js';

const KEYCLOAK_RETRY_INTERVAL = 5000;

export interface RejectionException extends Error {
    retry: boolean;
}

export async function AppInitializer(
    initializationService: InitializationService,
    envService: EnvironmentService,
    keycloak: KeycloakService,
    oidcProviderService: KeycloakOidcProviderService,
    sessionGuardService: SessionGuardService
) {
    try {
        initializationService.setInfo('Loading client configuration...');
        const config = await envService.loadConfiguration();

        initializationService.setInfo('Checking for duplicate local clients...');
        await sessionGuardService.canInitialize();

        initializationService.setInfo('Checking idm health...');
        // wait for initial keycloak health check to pass via initialized promise
        await oidcProviderService.initialized
            .then((init) => init ? Promise.resolve(true) :
                Promise.reject({ message: 'All IDM health checks failed', retry: true } as RejectionException));

        initializationService.setInfo('Checking Authentication...');
        let keycloakConfig: KeycloakConfig = {
            url: config.AUTH_URL,
            realm: config.AUTH_REALM,
            clientId: config.AUTH_CLIENT_ID
        };
        await keycloak.init({
            config: config.featureFlags?.ccAdminIntegrated ? { ...keycloakConfig } : { ...keycloakConfig, oidcProvider: oidcProviderService },
            initOptions: {
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
                silentCheckSsoFallback: false,
                checkLoginIframe: true,
                checkLoginIframeInterval: 5
            }
        }).catch((err) => Promise.reject({ ...err, retry: true } as RejectionException));

        initializationService.setInfo('Authentication complete beginning boostrap process...');
        initializationService.clear();
    } catch (err: any) {
        console.error(`Error during initialization`, err);
        if (err?.retry) {
            initializationService.setKeycloakError();
            initiateRetry(oidcProviderService);
        } else {
            initializationService.setError(err);
        }
    }
}

function initiateRetry(oidcProviderService: KeycloakOidcProviderService) {
    setTimeout(async () => {
        oidcProviderService.incrementStartingIndex();
        window.location.reload();
    }, KEYCLOAK_RETRY_INTERVAL);
}
